import React, { useMemo } from "react";
import { } from "native-injects";
import {
    GraphProps, filter, formatValueWithDisplay, getMetricValue,
} from "./base";
import { MetricsDashboardGraphTypeDisplayType, MetricsDashboardSortDirection } from "../../../__generated__/api";
import Styles from "../Reports.module.scss";

interface ColumnType {
    fieldId: string;
    label?: string;
}
export default function Table(props: GraphProps) {
    const { rowsData, columns } = useMemo(() => {
        const indexes = props.graph.dimensions?.map(row => props.columnIndexMap[row.fieldId]) ?? [];

        const dataRows = filter(props.data.rows, props.graph.filters, props.columnIndexMap);

        const rows = dataRows.groupBy(row => JSON.stringify(indexes.map(index => row.values[index])));

        const colData = (props.graph.dimensions as ColumnType[] ?? [])
            .concat(props.graph.metrics as ColumnType[] ?? []);

        const cols = colData.map(column => {
            if (column.label) {
                return column.label;
            }
            const columnIndex = props.columnIndexMap[column.fieldId];
            return props.data.columns[columnIndex].field.name;
        });

        let rowData = Object.values(rows).map(group => {
            const first = group.firstElement;

            const dimensions: (string | number | undefined)[] = indexes.map(columnIndex => {
                const val = first.values[columnIndex];
                return props.labelMap[val ?? ""] ?? val;
            });

            const metrics = props.graph.metrics?.map(metric => {
                const metricIndex = props.columnIndexMap[metric.fieldId];
                const data = group.map(row => row.values[metricIndex]).filterNull();
                const metricValue = getMetricValue(data, metric.aggregation);
                return formatValueWithDisplay(metricValue, metric.display ?? MetricsDashboardGraphTypeDisplayType.Number);
            }) ?? [];
            return dimensions.concat(metrics);
        });

        if (props.graph.sort?.isNotEmpty) {
            const dataIndexMap = colData.map(({ fieldId }, index) => ({ fieldId, index })).toIdMap("fieldId", "index");

            rowData = rowData.sortBy(...props.graph.sort.map(sort => ({
                key: dataIndexMap[sort.fieldId].toString() as keyof (string | number | undefined),
                direction: sort.direction === MetricsDashboardSortDirection.Ascending ? 1 : -1,
            })));
        }

        return {
            rowsData: rowData,
            columns: cols,
        };
    }, [props.data]);

    return (
        <div>
            {props.graph.title && (<strong className="mb-2 d-block ms-1 text-light">{props.graph.title}</strong>)}
            <table className="table w-100">
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th className={`text-blue-light fw-normal ${index === 0 ? "" : "text-center"}`}>
                                {col}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rowsData.limit(15).map((row, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={`row-${index}`}>
                            {row.map((value, columnIndex) => (
                                <td
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`row-${index}-${columnIndex}`}
                                    className={`text-gray-600 ${Styles["table-td"]} ${columnIndex === 0 ? "" : "text-center"}`}
                                >
                                    <div className="text-truncate">
                                        {value ?? "-"}
                                    </div>
                                </td>
                            ))}
                        </tr>
                    ))}

                </tbody>
                {rowsData.length > 15 && (
                    <tfoot>
                        <td colSpan={100} className="text-light text-end pe-2">
                            Showing first 15/
                            {rowsData.length}
                            {" "}
                            rows.
                        </td>
                    </tfoot>
                )}
            </table>
        </div>
    );
}
